import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { IConversionDetector } from '../contracts';

type ManagedListener = {
  readonly target: 'window' | 'document';
  readonly type: string;
  readonly handler: EventListener;
  readonly options?: boolean | AddEventListenerOptions;
};

export abstract class BaseAutoTracker implements IConversionDetector {
  protected readonly context: PixelRuntimeContext;

  private active = false;

  private readonly managedListeners: ManagedListener[] = [];

  private observer: MutationObserver | null = null;

  constructor(context: PixelRuntimeContext) {
    this.context = context;
  }

  init(): void {
    if (this.active) return;
    this.onInit();
    this.active = true;
  }

  destroy(): void {
    if (!this.active) return;
    this.onDestroy();
    this.removeAllManagedListeners();
    this.observer?.disconnect();
    this.observer = null;
    this.active = false;
  }

  protected abstract onInit(): void;

  protected abstract onDestroy(): void;

  protected isWindowAvailable(): boolean {
    return typeof window !== 'undefined';
  }

  protected isDocumentAvailable(): boolean {
    return typeof document !== 'undefined';
  }

  protected addWindowListener<K extends keyof WindowEventMap>(
    type: K,
    handler: (event: WindowEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!this.isWindowAvailable()) return;
    const listener = handler as EventListener;
    window.addEventListener(type, listener, options);
    this.managedListeners.push({
      target: 'window',
      type,
      handler: listener,
      options,
    });
  }

  protected addDocumentListener<K extends keyof DocumentEventMap>(
    type: K,
    handler: (event: DocumentEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!this.isDocumentAvailable()) return;
    const listener = handler as EventListener;
    document.addEventListener(type, listener, options);
    this.managedListeners.push({
      target: 'document',
      type,
      handler: listener,
      options,
    });
  }

  protected observeDom(callback: () => void): void {
    if (!this.isDocumentAvailable()) return;
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => callback());
    if (document.body) {
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  protected clearManagedTimeout(
    timer: ReturnType<typeof setTimeout> | null
  ): null {
    if (timer) clearTimeout(timer);
    return null;
  }

  protected removeAllManagedListeners(): void {
    for (const entry of this.managedListeners) {
      if (entry.target === 'window' && this.isWindowAvailable()) {
        window.removeEventListener(entry.type, entry.handler, entry.options);
      } else if (entry.target === 'document' && this.isDocumentAvailable()) {
        document.removeEventListener(entry.type, entry.handler, entry.options);
      }
    }
    this.managedListeners.length = 0;
  }
}
